/**
 * Python API Service
 * Connects to the Python FastAPI backend for leadership metrics
 */

import type {
  CrossTeamAggregation,
  DashboardFilters,
  DataSourceOptions,
  SignalName,
} from '@delivery-metrics/shared';

import { apiClient } from '../lib/api-client';
import type { KPISummaryUnit } from '../utils/kpiSummary';

export interface CrossTeamCompareResult {
  teams: string[];
  signals: Record<string, CrossTeamAggregation>;
}

export interface PythonKPIs {
  cycle_time_kpi: number;
  delivery_velocity_kpi: number;
  test_coverage_kpi: number;
  defect_escape_rate_kpi: number;
}

export interface MetricDataPoint {
  timestamp: string;
  label?: string;
  value: number;
}

export type MetricName =
  'cycle_time' | 'delivery_velocity' | 'test_coverage' | 'defect_escape_rate';

export interface LeadershipKPIOverview {
  cycle_time_reduction_pct: number;
  delivery_velocity_increase_pct: number;
  test_coverage_increase_pct: number;
  defect_escape_rate_reduction_pct: number;
  summaryUnits: Record<MetricName, KPISummaryUnit>;
  trendLines?: Partial<Record<SignalName, number[]>>;
}

interface LeadershipKPIResponseValue {
  current?: unknown;
  unit?: unknown;
}

interface LeadershipKPIResponse {
  innovationEffectiveness?: {
    cycleTimeReduction?: LeadershipKPIResponseValue;
    deliveryVelocityIncrease?: LeadershipKPIResponseValue;
    testCoverageIncrease?: LeadershipKPIResponseValue;
    defectEscapeRateReduction?: LeadershipKPIResponseValue;
  };
  trends?: Array<{
    metric?: unknown;
    trendLine?: unknown;
  }>;
}

const DEFAULT_KPI_SUMMARY_UNITS: Record<MetricName, KPISummaryUnit> = {
  cycle_time: 'percentage',
  delivery_velocity: 'percentage',
  test_coverage: 'percentage_points',
  defect_escape_rate: 'percentage_points',
};

const resolveKPISummaryUnit = (
  value: unknown,
  fallback: KPISummaryUnit
): KPISummaryUnit =>
  value === 'percentage' || value === 'percentage_points' ? value : fallback;

const createDefaultLeadershipKPIOverview = (): LeadershipKPIOverview => ({
  cycle_time_reduction_pct: 0,
  delivery_velocity_increase_pct: 0,
  test_coverage_increase_pct: 0,
  defect_escape_rate_reduction_pct: 0,
  summaryUnits: { ...DEFAULT_KPI_SUMMARY_UNITS },
  trendLines: {},
});

export class PythonApiService {
  private static async delay(ms: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  private static async postWithRetry<T>(
    url: string,
    payload: unknown,
    maxAttempts = 3
  ): Promise<{ success: boolean; data?: T }> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await apiClient.post<T>(url, payload, {
          withCredentials: false,
        });
      } catch (error: any) {
        lastError = error;
        const status = error?.response?.status;
        const isNetworkError = !error?.response;
        const isRetriableStatus = typeof status === 'number' && status >= 500;
        const shouldRetry =
          attempt < maxAttempts && (isNetworkError || isRetriableStatus);

        if (!shouldRetry) {
          throw error;
        }

        await this.delay(300 * attempt);
      }
    }

    throw lastError ?? new Error('Request failed');
  }

  private static buildFilterPayload(filters?: DashboardFilters): {
    programs: string[];
    units: string[];
    repositories: string[];
    area_paths: string[];
    sonarqube_projects: string[];
    start_date?: string;
    end_date?: string;
  } {
    const payload: {
      programs: string[];
      units: string[];
      repositories: string[];
      area_paths: string[];
      sonarqube_projects: string[];
      start_date?: string;
      end_date?: string;
    } = {
      programs: filters?.programs || [],
      units: filters?.units || [],
      repositories: filters?.repositories || [],
      area_paths: filters?.areaPaths || [],
      sonarqube_projects: filters?.sonarqubeProjects || [],
    };
    if (filters?.timeRange?.start) {
      const d = new Date(filters.timeRange.start);
      payload.start_date = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    }
    if (filters?.timeRange?.end) {
      const d = new Date(filters.timeRange.end);
      payload.end_date = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}T23:59:59Z`;
    }
    return payload;
  }

  /**
   * Get the data-source options (repositories, area paths, SonarQube projects)
   * available within the currently selected program/unit scope.
   */
  static async getAvailableDataSources(
    filters?: DashboardFilters
  ): Promise<DataSourceOptions> {
    const empty: DataSourceOptions = {
      repositories: [],
      areaPaths: [],
      sonarqubeProjects: [],
    };
    try {
      const payload = this.buildFilterPayload(filters);
      const response = await this.postWithRetry<DataSourceOptions>(
        '/api/metrics/data-sources',
        payload
      );

      if (!response.success || !response.data) {
        console.warn('No data sources available, returning empty options');
        return empty;
      }
      return {
        repositories: response.data.repositories ?? [],
        areaPaths: response.data.areaPaths ?? [],
        sonarqubeProjects: response.data.sonarqubeProjects ?? [],
      };
    } catch (error) {
      console.warn(
        'Failed to fetch data sources, returning empty options:',
        error
      );
      return empty;
    }
  }

  /**
   * Get all KPI summaries from Python API
   */
  static async getKPIs(filters?: DashboardFilters): Promise<PythonKPIs> {
    try {
      const [cycleTime, deliveryVelocity, testCoverage, defectEscapeRate] =
        await Promise.all([
          this.getCycleTimeMetric(filters),
          this.getDeliveryVelocityMetric(filters),
          this.getTestCoverageMetric(filters),
          this.getDefectEscapeRateMetric(filters),
        ]);

      const getLatestValue = (points: MetricDataPoint[]) => {
        if (!points.length) {
          return 0;
        }

        return points[points.length - 1]?.value ?? 0;
      };

      return {
        cycle_time_kpi: getLatestValue(cycleTime),
        delivery_velocity_kpi: getLatestValue(deliveryVelocity),
        test_coverage_kpi: getLatestValue(testCoverage),
        defect_escape_rate_kpi: getLatestValue(defectEscapeRate),
      };
    } catch (error) {
      console.warn('Failed to fetch KPIs, returning defaults:', error);
      return {
        cycle_time_kpi: 0,
        delivery_velocity_kpi: 0,
        test_coverage_kpi: 0,
        defect_escape_rate_kpi: 0,
      };
    }
  }

  /**
   * Get leadership KPI percentage summaries from Python API.
   */
  static async getLeadershipKPIs(
    filters?: DashboardFilters
  ): Promise<LeadershipKPIOverview> {
    try {
      const payload = this.buildFilterPayload(filters);
      const response = await this.postWithRetry<LeadershipKPIResponse>(
        '/api/metrics/leadership',
        payload
      );

      console.debug('Leadership KPIs raw response:', response);

      if (!response.success || !response.data) {
        console.warn('No leadership KPI data available, returning defaults');
        return createDefaultLeadershipKPIOverview();
      }

      const innovation = response.data.innovationEffectiveness ?? {};
      const trendMetricMap: Record<string, MetricName> = {
        cycleTimeReduction: 'cycle_time',
        deliveryVelocityIncrease: 'delivery_velocity',
        testCoverageIncrease: 'test_coverage',
        defectEscapeRateReduction: 'defect_escape_rate',
      };
      const trendLines: Partial<Record<SignalName, number[]>> = {};
      for (const trend of response.data.trends ?? []) {
        const metric =
          typeof trend.metric === 'string'
            ? trendMetricMap[trend.metric]
            : undefined;
        const trendLineValues = Array.isArray(trend.trendLine)
          ? trend.trendLine
          : null;
        const trendLine = trendLineValues?.filter(
          (value): value is number => typeof value === 'number'
        );
        if (
          metric &&
          trendLine &&
          trendLine.length === trendLineValues?.length
        ) {
          trendLines[metric] = trendLine;
        }
      }
      console.debug('Extracted innovation data:', innovation);

      const result: LeadershipKPIOverview = {
        cycle_time_reduction_pct: Number(
          innovation.cycleTimeReduction?.current ?? 0
        ),
        delivery_velocity_increase_pct: Number(
          innovation.deliveryVelocityIncrease?.current ?? 0
        ),
        test_coverage_increase_pct: Number(
          innovation.testCoverageIncrease?.current ?? 0
        ),
        defect_escape_rate_reduction_pct: Number(
          innovation.defectEscapeRateReduction?.current ?? 0
        ),
        summaryUnits: {
          cycle_time: resolveKPISummaryUnit(
            innovation.cycleTimeReduction?.unit,
            DEFAULT_KPI_SUMMARY_UNITS.cycle_time
          ),
          delivery_velocity: resolveKPISummaryUnit(
            innovation.deliveryVelocityIncrease?.unit,
            DEFAULT_KPI_SUMMARY_UNITS.delivery_velocity
          ),
          test_coverage: resolveKPISummaryUnit(
            innovation.testCoverageIncrease?.unit,
            DEFAULT_KPI_SUMMARY_UNITS.test_coverage
          ),
          defect_escape_rate: resolveKPISummaryUnit(
            innovation.defectEscapeRateReduction?.unit,
            DEFAULT_KPI_SUMMARY_UNITS.defect_escape_rate
          ),
        },
        trendLines,
      };
      console.debug('Transformed leadership KPIs:', result);
      return result;
    } catch (error) {
      console.warn(
        'Failed to fetch leadership KPIs, returning defaults:',
        error
      );
      return createDefaultLeadershipKPIOverview();
    }
  }

  /**
   * Get time-series data for a specific metric
   */
  static async getMetric(
    metricName: MetricName,
    filters?: DashboardFilters
  ): Promise<MetricDataPoint[]> {
    try {
      const payload = this.buildFilterPayload(filters);
      const response = await this.postWithRetry<MetricDataPoint[]>(
        `/api/metrics/${metricName}`,
        payload
      );

      if (!response.success || !response.data) {
        console.warn(`No data available for ${metricName} metric`);
        return [];
      }
      return response.data;
    } catch (error) {
      console.warn(
        `Failed to fetch ${metricName} metric, returning empty data:`,
        error
      );
      return [];
    }
  }

  /**
   * Get cycle time metric data
   */
  static async getCycleTimeMetric(
    filters?: DashboardFilters
  ): Promise<MetricDataPoint[]> {
    return this.getMetric('cycle_time', filters);
  }

  /**
   * Get delivery velocity metric data
   */
  static async getDeliveryVelocityMetric(
    filters?: DashboardFilters
  ): Promise<MetricDataPoint[]> {
    return this.getMetric('delivery_velocity', filters);
  }

  /**
   * Get test coverage metric data
   */
  static async getTestCoverageMetric(
    filters?: DashboardFilters
  ): Promise<MetricDataPoint[]> {
    return this.getMetric('test_coverage', filters);
  }

  /**
   * Get defect escape rate metric data
   */
  static async getDefectEscapeRateMetric(
    filters?: DashboardFilters
  ): Promise<MetricDataPoint[]> {
    return this.getMetric('defect_escape_rate', filters);
  }

  /**
   * Get cross-team aggregated KPI values
   */
  static async getCrossTeamAggregate(
    teams: string[],
    products: string[],
    signalNames: SignalName[],
    filters?: DashboardFilters
  ): Promise<CrossTeamAggregation[]> {
    try {
      const payload = {
        teams,
        products,
        signal_names: signalNames,
        ...this.buildFilterPayload(filters),
      };
      const response = await apiClient.post<CrossTeamAggregation[]>(
        '/api/metrics/cross-team/aggregate',
        payload
      );
      if (!response.success || !response.data) {
        return [];
      }
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch cross-team aggregate:', error);
      return [];
    }
  }

  /**
   * Get cross-team comparison (per-team KPI breakdowns side-by-side)
   */
  static async getCrossTeamCompare(
    teams: string[],
    products: string[],
    signalNames: SignalName[],
    filters?: DashboardFilters
  ): Promise<CrossTeamCompareResult | null> {
    try {
      const payload = {
        teams,
        products,
        signal_names: signalNames,
        ...this.buildFilterPayload(filters),
      };
      const response = await apiClient.post<CrossTeamCompareResult>(
        '/api/metrics/cross-team/compare',
        payload
      );
      if (!response.success || !response.data) {
        return null;
      }
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch cross-team comparison:', error);
      return null;
    }
  }

  /**
   * Clear API cache
   */
  static async clearCache(): Promise<void> {
    const response = await apiClient.post<{ status: string }>(
      '/api/cache/clear'
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to clear cache');
    }
  }
}
